import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import { FieldArray, Form, Formik, getIn } from 'formik'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import BanButton from '../BanButton'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import {
  isoDateStrToDateOrNull,
  dateToISO,
  phoneFilter,
} from '../../helpers'
import {
  CasePARespType,
  AttachmentListRespType,
  CaseCommonDataType,
  CaseReportId,
  AttachmentType,
  DamageType,
  PartType,
  DamageTypeEnum,
} from '../../models/caseReport'
import { useStyles } from '../../pages/styled'
import ButtonSimply from '../ButtonSimply'
import TextField from '../TextField'
import { DestinationPicker } from '../DestinationPicker'
import { DatePicker } from '../DatePicker'
import * as yup from 'yup'
import { Ga4Form } from './Ga4Form'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'
import { BooleanField } from '../BooleanField'

export interface AccidentInputDataType {
  caseCommon?: CaseCommonDataType | null
  casePA?: CasePARespType | null
}

export interface AccidentOutputDataType {
  caseCommon: CaseCommonDataType
  casePA: CasePARespType
}

export interface AccidentInputParamsType {
  caseReportId: CaseReportId
  data: AccidentInputDataType
  isPageConfirmInProgress: boolean
  errorMessage: string
  onPageContinue(value: AccidentOutputDataType): void
  onPageBack(): void
  notifyDataChanged(isChanged: boolean): void
}

const AccidentForm = (params: AccidentInputParamsType) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  const handleFormikSubmit = (value: any) => {
    const modelValue: AccidentOutputDataType =
      transformFormikValuesToOutput(value)
    params.onPageContinue(modelValue)
  }

  function transformFormikValuesToOutput(values: any): AccidentOutputDataType {
    const caseCommonFromFormik: CaseCommonDataType = {
      occurredOn: dateToISO(values.occurredOn),
      description: values.description,
      destination: values.destination ?? undefined,
      resort: values.resort,
      place: values.place,
      assistance: values.assistance ?? undefined,
      assistanceDesc:
        values.assistance ? values.assistanceDesc : undefined,
    }
    const damageType: DamageType = values.damageType ?? undefined
    const casePAFromFormik: CasePARespType = {
      damageType: damageType,
      claimReported: values.claimReported ?? undefined,
      claimNumber:
        values.claimReported ? values.claimNumber : undefined,
      treatedAbroad: values.treatedAbroad ?? undefined,
      treatmentFacility:
        values.treatedAbroad ? values.treatmentFacility : undefined,
      homeDoctorName: values.homeDoctorName,
      homeDoctorContact: values.homeDoctorContact,
      attachmentList: values.attachmentList,
    }
    return { caseCommon: caseCommonFromFormik, casePA: casePAFromFormik }
  }

  function transformInputToFormikValues(input: AccidentInputDataType): any {
    return {
      damageType: input.casePA?.damageType ?? null, // permanent disablement or death
      claimReported: input.casePA?.claimReported ?? null, // true or false, if true enter claim number
      claimNumber: input.casePA?.claimNumber ?? '',
      occurredOn: isoDateStrToDateOrNull(input.caseCommon?.occurredOn),
      destination: input.caseCommon?.destination ?? null,
      resort: input.caseCommon?.resort ?? '',
      place: input.caseCommon?.place ?? '',
      assistance: input.caseCommon?.assistance ?? null,
      assistanceDesc: input.caseCommon?.assistanceDesc ?? '',
      treatedAbroad: input.casePA?.treatedAbroad ?? null, // true or false, if true enter treatment facility
      treatmentFacility: input.casePA?.treatmentFacility ?? '',
      description: input.caseCommon?.description ?? '',
      homeDoctorName: input.casePA?.homeDoctorName ?? '',
      homeDoctorContact: input.casePA?.homeDoctorContact ?? '',
      attachmentList: input.casePA?.attachmentList ?? [],
    }
  }

  const formikInitialValues = transformInputToFormikValues(params.data)

  const AccidentValidationSchema = () =>
    yup.object().shape({
      damageType: yup.string().nullable().required(t('accidentForm.damageType.required')),
      claimReported: yup
        .boolean()
        .nullable()
        .required(t('accidentForm.claimReported.required')),
      claimNumber: yup.string().when('claimReported', {
        is: (claimReported: any) => claimReported === true,
        then: yup.string().required(t('accidentForm.claimNumber.required')),
      }),
      occurredOn: yup
        .date()
        .nullable()
        .required(t('accidentForm.occurredOn.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      destination: yup
        .string()
        .required(t('accidentForm.destination.required'))
        .nullable(),
      place: yup.string().required(t('accidentForm.place.required')),
      resort: yup
        .string()
        .required(t('accidentForm.resort.required'))
        .test('len', t('accidentForm.resort.length'), (val: any) => {
          return val?.length <= 50
        }),
      assistance: yup
        .boolean()
        .nullable()
        .required(t('optionRequired')),
      assistanceDesc: yup
        .mixed()
        .nullable()
        .when('assistance', {
          is: (assistance: any) => assistance === 'false',
          then: yup
            .string()
            .required(t('accidentForm.assistanceDesc.required'))
            .test('len', t('thirdPg.assistanceDesc.length'), (val: any) => {
              return val?.length <= 254
            }),
        }),
      treatedAbroad: yup.boolean().nullable().required(t('optionRequired')),
      treatmentFacility: yup.string().when('treatedAbroad', {
        is: (treatedAbroad: any) => treatedAbroad === true,
        then: yup
          .string()
          .required(t('accidentForm.treatmentFacility.required')),
      }),
      description: yup.string(),
      homeDoctorName: yup
        .string()
        .required(t('accidentForm.homeDoctorName.required')),
      homeDoctorContact: yup
        .string()
        .required(t('accidentForm.homeDoctorContact.required')),
      attachmentList: yup.array().of(
        yup.object().shape({
          attachmentType: yup
            .string()
            .nullable()
            .required(t('accidentForm.attachmentType.required')),
          attachments: yup.array().nullable().min(1, t('uploadFile.err1')),
        })
      ),
    })

  const formikValueForNewAttachmentList: AttachmentListRespType = {
    attachmentType: null,
    attachments: [],
  }
  if (
    formikInitialValues.attachmentList == null ||
    formikInitialValues.attachmentList.length === 0
  ) {
    formikInitialValues.attachmentList = [formikValueForNewAttachmentList]
  }

  const damageTypeLookup = useMemo(() =>
    objectToLookup(t('damageTypes', { returnObjects: true })).filter((item) =>
      [DamageTypeEnum.INJURY, DamageTypeEnum.DEATH].includes(item.code as DamageTypeEnum)
    )
    , [t])
  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'AccidentForm'} >
      <Formik
        initialValues={formikInitialValues}
        onSubmit={(values) => handleFormikSubmit(values)}
        validationSchema={AccidentValidationSchema}
        enableReinitialize={true}
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => {
          /**
           * Local truncation of repeating code - should be used within all fields
           */
          function isError(name: string): boolean {
            // console.log('isError:', name)
            return (
              getIn(errors, name) != null &&
              getIn(touched, name))
          }
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('accidentForm.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <RadioButtonsField
                          aria-label={'damageType'}
                          name={'damageType'}
                          label={t('accidentForm.damageType.label')}
                          required
                          value={values.damageType}
                          onChange={handleChange}
                          lookup={damageTypeLookup}
                          error={isError('damageType')}
                          helperText={errors.damageType && touched.damageType
                            ? errors.damageType
                            : null}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label={'claimReported'}
                          name={'claimReported'}
                          value={values.claimReported}
                          label={t('accidentForm.claimReported.label')}
                          onValueChange={(value) => setFieldValue('claimReported', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          error={!!(errors.claimReported && touched.claimReported)}
                          helperText={
                            errors.claimReported && touched.claimReported ? errors.card : null
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.claimReported && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        className={classes.gridItem}
                      >
                        <Grid item xs={12} sm={12} xl={12}>
                          <TextField
                            // @ts-ignore
                            label={t('accidentForm.claimNumber.label')}
                            placeholder={t('accidentForm.claimNumber.hint')}
                            error={!!(errors.claimNumber && touched.claimNumber)}
                            name="claimNumber"
                            value={values.claimNumber}
                            onChange={handleChange}
                            helperText={
                              errors.claimNumber && touched.claimNumber
                                ? errors.claimNumber
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <DatePicker
                          name="occurredOn"
                          label={t('accidentForm.occurredOn.label')}
                          value={values.occurredOn}
                          required
                          minDate={new Date('2000, 0, 1')}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          error={Boolean(errors.occurredOn && touched.occurredOn)}
                          helperText={
                            errors.occurredOn && touched.occurredOn
                              ? errors.occurredOn
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.fieldSize}
                      >
                        <DestinationPicker
                          name="destination"
                          label={t('accidentForm.destination.label')}
                          selectedItemCode={values.destination}
                          required
                          onValueChanged={(
                            selectedDestination: string | null
                          ) => {
                            setFieldValue('destination', selectedDestination)
                          }}
                          onBlur={handleBlur}
                          helperText={
                            errors.destination && touched.destination
                              ? errors.destination
                              : null
                          }
                          error={Boolean(
                            errors.destination && touched.destination
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="resort"
                          value={values.resort}
                          label={t('accidentForm.resort.label')}
                          placeholder={t('accidentForm.resort.hint')}
                          required
                          // no maxLength needed as not stored to DB
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.resort && touched.resort)}
                          helperText={
                            errors.resort && touched.resort ? errors.resort : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="place"
                          value={values.place}
                          label={t('accidentForm.place.label')}
                          placeholder={t('accidentForm.place.hint')}
                          required
                          inputProps={{
                            maxLength: 50,
                          }}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={!!(errors.place && touched.place)}
                          helperText={
                            errors.place && touched.place ? errors.place : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="assistance"
                          name="assistance"
                          value={values.assistance}
                          label={t('accidentForm.assistance.label')}
                          onValueChange={(value) => setFieldValue('assistance', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          noFirst
                          error={isError('assistance')}
                          helperText={isError('assistance') ? errors.assistance : null}
                        />
                      </Grid>
                    </Grid>
                    {!values.assistance && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={12}
                          xl={12}
                          xs={12}
                          className={classes.fieldSize}
                        >
                          <TextField
                            // @ts-ignore
                            label={t('accidentForm.assistanceDesc.label')}
                            placeholder={t('accidentForm.assistanceDesc.hint')}
                            error={Boolean(
                              errors.assistanceDesc && touched.assistanceDesc)
                            }
                            name="assistanceDesc"
                            value={values.assistanceDesc}
                            onChange={handleChange}
                            helperText={
                              errors.assistanceDesc && touched.assistanceDesc
                                ? errors.assistanceDesc
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="treatedAbroad"
                          name="treatedAbroad"
                          value={values.treatedAbroad}
                          label={t('accidentForm.treatedAbroad.label')}
                          onValueChange={(value) => setFieldValue('treatedAbroad', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          error={!!(errors.treatedAbroad && touched.treatedAbroad)}
                          helperText={
                            errors.treatedAbroad && touched.treatedAbroad ? errors.treatedAbroad : null
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.treatedAbroad && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid item sm={12} xl={12} xs={12}>
                          <TextField
                            name="treatmentFacility"
                            value={values.treatmentFacility}
                            label={t('accidentForm.treatmentFacility.label')}
                            placeholder={t('accidentForm.treatmentFacility.hint')}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!(
                              errors.treatmentFacility &&
                              touched.treatmentFacility)
                            }
                            helperText={
                              errors.treatmentFacility &&
                                touched.treatmentFacility
                                ? errors.treatmentFacility
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <TextField
                          label={t('accidentForm.description.label')}
                          aria-label="description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          multiline
                          rows={7}
                          rowsMax={7}
                          placeholder={t('accidentForm.description.hint')}
                          className={classes.textAreaClass}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="homeDoctorName"
                          value={values.homeDoctorName}
                          label={t('accidentForm.homeDoctorName.label')}
                          placeholder={t('accidentForm.homeDoctorName.hint')}
                          required
                          inputProps={{
                            maxLength: 50,
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(errors.homeDoctorName && touched.homeDoctorName)}
                          helperText={
                            errors.homeDoctorName && touched.homeDoctorName
                              ? errors.homeDoctorName
                              : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="homeDoctorContact"
                          value={values.homeDoctorContact}
                          label={t('accidentForm.homeDoctorContact.label')}
                          placeholder={t('accidentForm.homeDoctorContact.hint')}
                          regex={phoneFilter}
                          required
                          inputProps={{
                            maxLength: 50,
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!(
                            errors.homeDoctorContact && touched.homeDoctorContact)
                          }
                          helperText={
                            errors.homeDoctorContact && touched.homeDoctorContact
                              ? errors.homeDoctorContact
                              : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                        />
                      </Grid>
                    </Grid>
                    {values?.attachmentList && (
                      <FieldArray
                        name="attachmentList"
                        render={({ push, remove }) => (
                          <React.Fragment>
                            {values?.attachmentList &&
                              Boolean(values?.attachmentList.length) &&
                              values?.attachmentList.map(
                                (document: any, index: any) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.rowAlign}
                                        >
                                          <Typography
                                            className={classes.text}
                                            variant="h3"
                                          >
                                            {index > 0 ? index + 1 + '. ' : ''}
                                            {t('accidentForm.documentItemTitle')}
                                          </Typography>
                                          {index > 0 && (
                                            <BanButton
                                              onClick={() => remove(index)}
                                            >
                                              <DeleteIcon />
                                            </BanButton>
                                          )}
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.row}
                                        >
                                          <FormControl
                                            component="fieldset"
                                            disabled={
                                              values.attachmentList[index]
                                                .attachments.length > 0
                                                ? true
                                                : false
                                            }
                                          >
                                            <FormLabel
                                              required
                                              error={Boolean(
                                                getIn(
                                                  errors,
                                                  `attachmentList[${index}].attachmentType`
                                                ) &&
                                                getIn(
                                                  touched,
                                                  `attachmentList[${index}].attachmentType`
                                                ))
                                              }
                                            >
                                              {t(
                                                'accidentForm.attachmentType.label'
                                              )}
                                            </FormLabel>
                                            <RadioGroup
                                              name={`attachmentList[${index}].attachmentType`}
                                              value={
                                                values.attachmentList[index]
                                                  .attachmentType
                                              }
                                              onChange={(event, value) => {
                                                setFieldValue(
                                                  `attachmentList[${index}].attachmentType`,
                                                  event.target.value ?? ''
                                                )
                                              }}
                                              className={classes.row}
                                            >
                                              <FormControlLabel
                                                value={AttachmentType.MEDICPL}
                                                control={<Radio />}
                                                label={t(
                                                  'attachments.CASEPA.MEDICPL'
                                                )}
                                              />
                                              <FormControlLabel
                                                value={AttachmentType.MEDICABROAD}
                                                control={<Radio />}
                                                label={t(
                                                  'attachments.CASEPA.MEDICABROAD'
                                                )}
                                              />
                                              <FormControlLabel
                                                value={AttachmentType.REPORT}
                                                control={<Radio />}
                                                label={t(
                                                  'attachments.CASEPA.REPORT'
                                                )}
                                              />
                                              <FormControlLabel
                                                value={AttachmentType.OTHER}
                                                control={<Radio />}
                                                label={t(
                                                  'attachments.CASEPA.OTHER'
                                                )}
                                              />
                                            </RadioGroup>
                                            {getIn(
                                              errors,
                                              `attachmentList[${index}].attachmentType`
                                            ) &&
                                              getIn(
                                                touched,
                                                `attachmentList[${index}].attachmentType`
                                              ) && (
                                                <div
                                                  className={classes.checkError}
                                                >
                                                  {getIn(
                                                    errors,
                                                    `attachmentList[${index}].attachmentType`
                                                  )}
                                                </div>
                                              )}
                                          </FormControl>
                                        </Grid>
                                        <Attachments
                                          name={`attachmentList[${index}].attachments`}
                                          caseReportId={params.caseReportId}
                                          labelText={t(
                                            'accidentForm.uploadDocument'
                                          )}
                                          attachmentType={
                                            values.attachmentList[index]
                                              .attachmentType
                                          }
                                          part={PartType.CASEPA}
                                          //itemOrderNum=undefined
                                          attachments={
                                            values.attachmentList[index]
                                              .attachments
                                          }
                                          onAttachmentsChanged={(
                                            event: AttachmentsChangedEvent
                                          ) => {
                                            /*console.log(
                                              'onAttachmentsChanged: ',
                                              event
                                            )*/
                                            // aktualizace values formiku
                                            setFieldValue(
                                              `attachmentList[${index}].attachments`,
                                              event.value.attachments
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          required
                                          disabled={values.attachmentList[index]
                                            .attachmentType == null}
                                          error={isError(`attachmentList[${index}].attachments`)
                                          }
                                          helperText={
                                            getIn(
                                              errors,
                                              `attachmentList[${index}].attachments`
                                            ) &&
                                              getIn(
                                                touched,
                                                `attachmentList[${index}].attachments`
                                              )
                                              ? getIn(
                                                errors,
                                                `attachmentList[${index}].attachments`
                                              )
                                              : null
                                          }
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.row}
                                      >
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <ButtonSimply
                                            onClick={() => {
                                              const initialValue = {
                                                ...formikValueForNewAttachmentList,
                                              }
                                              push(initialValue)
                                            }}
                                          >
                                            {t('accidentForm.addDocument')}
                                          </ButtonSimply>
                                        </Grid>
                                      </Grid>
                                    </React.Fragment>
                                  )
                                }
                              )}
                          </React.Fragment>
                        )}
                      />
                    )}
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      backLinkText={t('accidentForm.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default AccidentForm
